// src/features/analytics/SchedulesTab.tsx
// Report schedules — automated periodic report generation. ListReportSchedules,
// CreateReportSchedule, UpdateReportSchedule, DeleteReportSchedule. Create/edit/
// delete require the "admin" relation server-side (see analytics.proto); list
// is member-visible, so the tab itself is not route-gated — the backend enforces it.

import { useEffect, useState } from 'react';
import { useI18n } from '@react/hooks';
import { Button, Badge, Alert, Input } from '@react/components';
import { Select } from '@/components/Select';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { analyticsApi } from '@core/api';
import type { Actor } from '@core/types/actor';
import type { Report, ReportDelivery, ReportSchedule } from '@core/types/analytics';
import { AnalyticsModal } from './Modal';
import { errMessage, formatDate, listParams, makeEntity, REPORT_DELIVERIES, type ScopeIds } from './helpers';

interface SchedulesTabProps {
  scope: ScopeIds;
}

export function SchedulesTab({ scope }: SchedulesTabProps) {
  const { t, locale } = useI18n();
  const [schedules, setSchedules] = useState<ReportSchedule[]>([]);
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ReportSchedule | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<ReportSchedule | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [reportId, setReportId] = useState('');
  const [cron, setCron] = useState('0 9 * * MON');
  const [timezone, setTimezone] = useState('UTC');
  const [delivery, setDelivery] = useState<ReportDelivery>('REPORT_DELIVERY_EMAIL');
  const [isActive, setIsActive] = useState(true);
  const [recipientDraft, setRecipientDraft] = useState('');
  const [recipients, setRecipients] = useState<Actor[]>([]);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    setError(null);
    Promise.all([analyticsApi.ListReportSchedules(listParams({ limit: 50 })), analyticsApi.ListReports(listParams({ limit: 50 }))])
      .then(([sched, rep]) => {
        setSchedules(sched.items?.items ?? []);
        setReports(rep.items?.items ?? []);
      })
      .catch((e: unknown) => setError(errMessage(e, t('analytics.schedules.loadError'))))
      .finally(() => setLoading(false));
  };

  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openCreate = () => {
    setEditing(null);
    setName('');
    setDescription('');
    setReportId(reports[0]?.entity?.id ?? '');
    setCron('0 9 * * MON');
    setTimezone('UTC');
    setDelivery('REPORT_DELIVERY_EMAIL');
    setIsActive(true);
    setRecipients([]);
    setRecipientDraft('');
    setFormError(null);
    setDialogOpen(true);
  };

  const openEdit = (s: ReportSchedule) => {
    setEditing(s);
    setName(s.name);
    setDescription(s.description);
    setReportId(s.report?.id ?? '');
    setCron(s.cron_expression);
    setTimezone(s.timezone || 'UTC');
    setDelivery(s.delivery ?? 'REPORT_DELIVERY_EMAIL');
    setIsActive(s.is_active);
    setRecipients(s.recipients ?? []);
    setRecipientDraft('');
    setFormError(null);
    setDialogOpen(true);
  };

  const addRecipient = () => {
    const v = recipientDraft.trim();
    if (v) setRecipients([...recipients, { display_name: v, avatar: '' }]);
    setRecipientDraft('');
  };

  const save = async () => {
    if (!name.trim() || !cron.trim() || !reportId) {
      setFormError(t('analytics.schedules.requiredFields'));
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      if (editing?.entity?.id) {
        const res = await analyticsApi.UpdateReportSchedule(editing.entity.id, {
          schedule: {
            ...editing,
            name: name.trim(),
            description,
            report: makeEntity(reportId, 'report', scope),
            cron_expression: cron,
            timezone,
            recipients,
            delivery,
            is_active: isActive,
          },
        });
        if (res.schedule) setSchedules((prev) => prev.map((s) => (s.entity?.id === res.schedule!.entity?.id ? res.schedule! : s)));
      } else {
        const res = await analyticsApi.CreateReportSchedule({
          entity: makeEntity('', 'report_schedule', scope),
          name: name.trim(),
          description,
          report: makeEntity(reportId, 'report', scope),
          cron_expression: cron,
          timezone,
          recipients,
          delivery,
        });
        if (res.schedule) setSchedules((prev) => [res.schedule!, ...prev]);
      }
      setDialogOpen(false);
    } catch (e) {
      setFormError(errMessage(e, t('analytics.schedules.saveError')));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete?.entity?.id) return;
    setDeleting(true);
    try {
      await analyticsApi.DeleteReportSchedule(confirmDelete.entity.id);
      setSchedules((prev) => prev.filter((s) => s.entity?.id !== confirmDelete.entity?.id));
    } catch (e) {
      setError(errMessage(e, t('analytics.schedules.deleteError')));
    } finally {
      setDeleting(false);
      setConfirmDelete(null);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-end">
        <Button size="sm" onClick={openCreate} disabled={reports.length === 0}>
          {t('analytics.schedules.create')}
        </Button>
      </div>

      {error && <Alert variant="error">{error}</Alert>}

      {loading && (
        <div className="space-y-2" aria-busy="true">
          <div className="h-14 animate-pulse rounded-md bg-muted" />
        </div>
      )}

      {!loading && schedules.length === 0 && !error && (
        <div className="rounded-md border border-dashed border-border p-8 text-center">
          <p className="text-sm text-muted-foreground">{t('analytics.schedules.empty')}</p>
        </div>
      )}

      <div className="space-y-2">
        {schedules.map((s) => (
          <div key={s.entity?.id} className="rounded-md border border-border p-3">
            <div className="flex items-center justify-between gap-2">
              <span className="font-medium text-foreground">{s.name}</span>
              <Badge variant={s.is_active ? 'success' : 'secondary'}>{s.is_active ? t('analytics.schedules.active') : t('analytics.schedules.inactive')}</Badge>
            </div>
            <div className="mt-1 flex flex-wrap gap-3 text-xs text-muted-foreground">
              <span className="font-mono">{s.cron_expression}</span>
              <span>{s.timezone}</span>
              <span>{t(`analytics.delivery.${s.delivery}`)}</span>
              {s.next_generate_at && <span>{t('analytics.schedules.next')}: {formatDate(s.next_generate_at, locale)}</span>}
            </div>
            <div className="mt-2 flex gap-2">
              <Button size="sm" variant="secondary" onClick={() => openEdit(s)}>
                {t('common.edit')}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(s)}>
                {t('common.delete')}
              </Button>
            </div>
          </div>
        ))}
      </div>

      <AnalyticsModal
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={editing ? t('analytics.schedules.edit') : t('analytics.schedules.create')}
        footer={
          <>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={save} disabled={saving}>
              {saving ? t('common.saving') : t('common.save')}
            </Button>
          </>
        }
      >
        {formError && (
          <Alert variant="error" className="mb-3">
            {formError}
          </Alert>
        )}
        <div className="space-y-3">
          <Input label={t('analytics.schedules.name')} value={name} onChange={(e) => setName(e.target.value)} required />
          <Select label={t('analytics.schedules.report')} value={reportId} onChange={(e) => setReportId(e.target.value)}>
            <option value="">{t('common.select')}</option>
            {reports.map((r) => (
              <option key={r.entity?.id} value={r.entity?.id}>
                {r.name}
              </option>
            ))}
          </Select>
          <div className="grid grid-cols-2 gap-3">
            <Input label={t('analytics.schedules.cron')} value={cron} onChange={(e) => setCron(e.target.value)} helperText="0 9 * * MON" />
            <Input label={t('analytics.schedules.timezone')} value={timezone} onChange={(e) => setTimezone(e.target.value)} />
          </div>
          <Select label={t('analytics.schedules.delivery')} value={delivery} onChange={(e) => setDelivery(e.target.value as ReportDelivery)}>
            {REPORT_DELIVERIES.map((d) => (
              <option key={d} value={d}>
                {t(`analytics.delivery.${d}`)}
              </option>
            ))}
          </Select>
          <div>
            <span className="mb-1 block text-xs font-medium text-muted-foreground">{t('analytics.schedules.recipients')}</span>
            <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-input bg-background p-2">
              {recipients.map((r, i) => (
                <span key={i} className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-0.5 text-xs text-foreground">
                  {r.display_name}
                  <button type="button" onClick={() => setRecipients(recipients.filter((_, idx) => idx !== i))} aria-label={t('common.remove')} className="text-muted-foreground hover:text-destructive">
                    <svg width="10" height="10" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                      <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                  </button>
                </span>
              ))}
              <input
                type="text"
                value={recipientDraft}
                onChange={(e) => setRecipientDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ',') {
                    e.preventDefault();
                    addRecipient();
                  }
                }}
                onBlur={addRecipient}
                placeholder={recipients.length === 0 ? t('analytics.schedules.recipientsPlaceholder') : undefined}
                className="min-w-[8rem] flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
              />
            </div>
          </div>
          {editing && <ToggleSwitch label={t('analytics.schedules.active')} checked={isActive} onChange={setIsActive} />}
        </div>
      </AnalyticsModal>

      <ConfirmDialog
        open={!!confirmDelete}
        title={t('analytics.schedules.deleteConfirmTitle')}
        description={t('analytics.schedules.deleteConfirmBody', { name: confirmDelete?.name ?? '' })}
        destructive
        isConfirming={deleting}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
}
